/**
 * ConfigProvisionerService — client facade for lazy AppBase data sources.
 *
 * AppBases are created server-side with only their config database.
 * Standard databases are provisioned by the backend on demand, when a feature
 * or preset declares that it needs them; this service requests them, verifies
 * the server-owned bindings and waits until their storage is locally ready.
 */
import {
  GraphQLService,
  type WorkspaceServerService,
} from '@affine/core/modules/cloud';
import { ensureAppBaseDataSourcesMutation } from '@affine/graphql';
import { isDatabaseStorageModelReady } from '@blocksuite/affine/blocks/database';
import { createDatabaseBlockDataSource } from '@blocksuite/affine-block-database';
import type { Store } from '@blocksuite/store';
import {
  createServerDatabaseRootDocId,
  isValidDatabaseBlockId,
  normalizeDatabaseBlockId,
} from '@streamlain/database-identity';
import {
  type AppBaseConfig,
  AppBaseConfigSchema,
  type AppBaseContentModel,
  AppBaseDatabaseBindingSchema,
  createAppBaseDataSourceKey,
  getDefaultAppBaseContentModelForRole,
  type StandardDatabaseRole,
} from '@streamlain/view-config';
import { Service } from '@toeverything/infra';

import type { DocsService } from '../../doc';
import { getAppBaseDataSources } from './app-base-standard-database-schema';
import { isDatabaseSnapshotFullyLoaded } from './database-snapshot-readiness';

type ProvisionedDatabaseRole = Exclude<StandardDatabaseRole, 'config'>;

const STANDARD_DATABASE_READY_TIMEOUT_MS = 5000;
const STANDARD_DATABASE_READY_POLL_MS = 50;

function isServerOwnedDataSourceAccessNotReady(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < 4 && current; depth += 1) {
    if (
      typeof current === 'object' &&
      current !== null &&
      'failureClass' in current &&
      current.failureClass === 'permission_lag'
    ) {
      return true;
    }
    if (
      current instanceof Error &&
      (current.message.includes('ACCESS_DENIED') ||
        current.message.includes('permission_lag'))
    ) {
      return true;
    }
    current =
      typeof current === 'object' && current !== null && 'cause' in current
        ? current.cause
        : null;
  }
  return false;
}

function isCloudDocStorageConnectionNotReady(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < 4 && current; depth += 1) {
    if (
      current instanceof Error &&
      current.message.includes(
        'CloudDocStorageConnection has not been established'
      )
    ) {
      return true;
    }
    current =
      typeof current === 'object' && current !== null && 'cause' in current
        ? current.cause
        : null;
  }
  return false;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function normalizeProvisionedDatabaseBlockId(
  value: string | undefined,
  context: string
): string | undefined {
  if (value === undefined) return undefined;
  const normalized = normalizeDatabaseBlockId(value);
  if (!isValidDatabaseBlockId(normalized)) {
    throw new Error(
      `ConfigProvisioner: ${context} must be a database block id, received "${value}"`
    );
  }
  return normalized;
}

function normalizeAppBaseConfigDataSourceRefs(
  appBaseConfig: AppBaseConfig
): AppBaseConfig {
  let bindingsChanged = false;
  const databaseBindings = Object.fromEntries(
    Object.entries(appBaseConfig.databaseBindings).map(
      ([sourceKey, binding]) => {
        const databaseId = normalizeProvisionedDatabaseBlockId(
          binding.databaseId,
          `database binding ${sourceKey} databaseId`
        )!;
        const blockId = normalizeProvisionedDatabaseBlockId(
          binding.placement.blockId,
          `database binding ${sourceKey} blockId`
        )!;
        const originalBlockId = normalizeProvisionedDatabaseBlockId(
          binding.originalPlacement.blockId,
          `database binding ${sourceKey} original blockId`
        )!;
        if (
          databaseId === binding.databaseId &&
          blockId === binding.placement.blockId &&
          originalBlockId === binding.originalPlacement.blockId
        ) {
          return [sourceKey, binding] as const;
        }
        bindingsChanged = true;
        return [
          sourceKey,
          AppBaseDatabaseBindingSchema.parse({
            ...binding,
            databaseId,
            placement: {
              ...binding.placement,
              blockId,
            },
            originalPlacement: {
              ...binding.originalPlacement,
              blockId: originalBlockId,
            },
          }),
        ] as const;
      }
    )
  );
  if (!bindingsChanged) return appBaseConfig;
  return AppBaseConfigSchema.parse({
    ...appBaseConfig,
    databaseBindings,
  });
}

export class ConfigProvisionerService extends Service {
  private readonly ensureStandardDatabasesInFlight = new Map<
    string,
    Promise<AppBaseConfig>
  >();

  constructor(
    private readonly docsService: DocsService,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super();
  }

  async ensureStandardDatabase(input: {
    workspaceId?: string;
    appBaseId?: string;
    docId: string;
    appBaseConfig: AppBaseConfig;
    role: ProvisionedDatabaseRole;
    sourceKey?: string;
    contentModel?: AppBaseContentModel;
    label?: string;
  }): Promise<AppBaseConfig> {
    return this.ensureStandardDatabases({
      workspaceId: input.workspaceId,
      appBaseId: input.appBaseId,
      docId: input.docId,
      appBaseConfig: input.appBaseConfig,
      requests: [
        {
          role: input.role,
          ...(input.sourceKey ? { sourceKey: input.sourceKey } : {}),
          ...(input.contentModel ? { contentModel: input.contentModel } : {}),
          label: input.label,
        },
      ],
    });
  }

  async ensureStandardDatabases(input: {
    reconcileInstalledSchemas?: boolean;
    workspaceId?: string;
    appBaseId?: string;
    docId: string;
    appBaseConfig: AppBaseConfig;
    requests: Array<{
      role: ProvisionedDatabaseRole;
      sourceKey?: string;
      contentModel?: AppBaseContentModel;
      label?: string;
    }>;
  }): Promise<AppBaseConfig> {
    void input.docId;
    if (!input.workspaceId || !input.appBaseId) {
      throw new Error(
        'ConfigProvisioner: workspaceId and appBaseId are required for server-owned data source provisioning'
      );
    }
    const inputAppBaseConfig = normalizeAppBaseConfigDataSourceRefs(
      input.appBaseConfig
    );
    const inputDataSources = getAppBaseDataSources(inputAppBaseConfig);
    const workspaceId = input.workspaceId;
    const appBaseId = input.appBaseId;
    const requests = input.requests.map(request => {
      const existingRef = request.sourceKey
        ? inputDataSources[request.sourceKey]
        : (Object.values(inputDataSources).find(
            ref => ref.role === request.role
          ) ?? null);
      const contentModel: AppBaseContentModel =
        request.contentModel ??
        existingRef?.contentModel ??
        getDefaultAppBaseContentModelForRole(request.role);
      return {
        role: request.role,
        sourceKey:
          request.sourceKey ??
          existingRef?.key ??
          createAppBaseDataSourceKey({ role: request.role, contentModel }),
        contentModel,
        ...(request.label ? { label: request.label } : {}),
      };
    });
    for (const request of requests) {
      const existingRef = inputDataSources[request.sourceKey];
      if (
        existingRef &&
        (existingRef.role !== request.role ||
          existingRef.contentModel !== request.contentModel)
      ) {
        throw new Error(
          `ConfigProvisioner: data source ${request.sourceKey} is ${existingRef.role}/${existingRef.contentModel}, expected ${request.role}/${request.contentModel}`
        );
      }
    }

    const missingRequests = requests.filter(
      request => !inputDataSources[request.sourceKey]
    );
    const ensureThroughServer = async (
      serverRequests: typeof requests
    ): Promise<AppBaseConfig> => {
      const ensureKey = this.createStandardDatabasesEnsureKey({
        appBaseId,
        requests: serverRequests,
        workspaceId,
      });
      const inFlight = this.ensureStandardDatabasesInFlight.get(ensureKey);
      if (inFlight) return inFlight;

      const promise = (async () => {
        const result = await this.requireGraphQL().gql({
          query: ensureAppBaseDataSourcesMutation,
          variables: {
            workspaceId,
            id: appBaseId,
            input: {
              requests: serverRequests,
            },
          },
        });
        const appBaseConfig = normalizeAppBaseConfigDataSourceRefs(
          AppBaseConfigSchema.parse(result.ensureAppBaseDataSources)
        );
        this.assertServerOwnedDataSourcesPresent(appBaseConfig, serverRequests);
        await this.waitForDataSourcesReady(
          appBaseConfig,
          serverRequests.map(request => request.sourceKey),
          workspaceId,
          { retryPermissionLag: true }
        );
        return appBaseConfig;
      })();
      this.ensureStandardDatabasesInFlight.set(ensureKey, promise);
      try {
        return await promise;
      } finally {
        if (this.ensureStandardDatabasesInFlight.get(ensureKey) === promise) {
          this.ensureStandardDatabasesInFlight.delete(ensureKey);
        }
      }
    };
    if (input.reconcileInstalledSchemas) return ensureThroughServer(requests);
    if (missingRequests.length === 0) {
      try {
        await this.waitForDataSourcesReady(
          inputAppBaseConfig,
          requests.map(request => request.sourceKey),
          workspaceId
        );
        return inputAppBaseConfig;
      } catch (error) {
        if (!isServerOwnedDataSourceAccessNotReady(error)) throw error;
        // A second local-first client can observe the new data-source refs
        // before the creating client's server-owned mutation group commits.
        // Re-enter the serialized, idempotent server ensure contract once;
        // its receipt is the authoritative access/readiness barrier.
        return ensureThroughServer(requests);
      }
    }
    return ensureThroughServer(missingRequests);
  }

  private createStandardDatabasesEnsureKey(input: {
    workspaceId: string;
    appBaseId: string;
    requests: ReadonlyArray<{
      role: ProvisionedDatabaseRole;
      sourceKey: string;
      contentModel: AppBaseContentModel;
    }>;
  }): string {
    const requestKey = input.requests
      .map(
        request =>
          `${request.sourceKey}:${request.role}:${request.contentModel}`
      )
      .sort((a, b) => a.localeCompare(b))
      .join('|');
    return `${input.workspaceId}:${input.appBaseId}:${requestKey}`;
  }

  private assertServerOwnedDataSourcesPresent(
    appBaseConfig: AppBaseConfig,
    requests: ReadonlyArray<{
      sourceKey: string;
    }>
  ): void {
    const dataSources = getAppBaseDataSources(appBaseConfig);
    for (const request of requests) {
      if (dataSources[request.sourceKey]) continue;
      throw new Error(
        `ConfigProvisioner: server response did not include data source ${request.sourceKey}`
      );
    }
  }

  private requireGraphQL(): GraphQLService {
    const graphql =
      this.workspaceServerService.server?.scope.get(GraphQLService) ?? null;
    if (!graphql) {
      throw new Error(
        'ConfigProvisioner: GraphQL service is not available for this workspace'
      );
    }
    return graphql;
  }

  private async waitForStandardDatabaseReady(
    bsDoc: Store,
    input:
      | string
      | {
          blockId: string;
          databaseId: string;
          workspaceId: string;
        },
    observeReadiness?: (state: {
      hostPresent: boolean;
      rootPresent: boolean;
      rootReady: boolean;
      storageReady: boolean;
      rowWindowReady: boolean | null;
      snapshotFullyLoaded: boolean | null;
    }) => void
  ): Promise<boolean> {
    const deadline = Date.now() + STANDARD_DATABASE_READY_TIMEOUT_MS;
    let observedModel: object | null = null;
    let observedDataSource: ReturnType<
      typeof createDatabaseBlockDataSource
    > | null = null;
    try {
      while (Date.now() <= deadline) {
        const blockId = typeof input === 'string' ? input : input.blockId;
        const hostModel = bsDoc.getBlock(blockId)?.model;
        const rootDocId =
          typeof input === 'string'
            ? null
            : createServerDatabaseRootDocId({
                workspaceId: input.workspaceId,
                databaseId: input.databaseId,
              });
        const workspace = (
          bsDoc as Store & {
            workspace?: {
              createDoc?: (id: string) => {
                getStore?: (options: { id: string }) => Store;
                load?: () => void;
                ready?: boolean;
              };
              getDoc?: (id: string) => {
                getStore?: (options: { id: string }) => Store;
                load?: () => void;
                ready?: boolean;
              } | null;
              registerDoc?: (id: string) => unknown;
            };
          }
        ).workspace;
        let root = rootDocId ? (workspace?.getDoc?.(rootDocId) ?? null) : null;
        if (rootDocId && !root) {
          workspace?.registerDoc?.(rootDocId);
          root = workspace?.getDoc?.(rootDocId) ?? null;
        }
        if (rootDocId && !root) {
          root = workspace?.createDoc?.(rootDocId) ?? null;
        }
        if (root && !root.ready) root.load?.();
        const rootModel = rootDocId
          ? root
              ?.getStore?.({ id: rootDocId })
              ?.getBlock(typeof input === 'string' ? blockId : input.databaseId)
              ?.model
          : null;
        const model = hostModel ?? rootModel;
        const storageReady = Boolean(
          model && isDatabaseStorageModelReady(model as never)
        );
        let rowWindowReady: boolean | null = null;
        let snapshotFullyLoaded: boolean | null = null;
        if (model && storageReady) {
          if (observedModel !== model) {
            observedDataSource?.dispose();
            observedModel = model;
            observedDataSource = createDatabaseBlockDataSource(model as never);
          }
          // The readiness API owns cold row-shard registration/loading. Reading
          // the snapshot directly can otherwise poll an empty shell forever.
          // Keep one datasource for the whole bounded wait: empty-shard repair
          // deliberately confirms the same snapshot across multiple polls.
          const dataSource = observedDataSource;
          if (!dataSource) continue;
          rowWindowReady = dataSource.isInitialRowWindowReady();
          snapshotFullyLoaded = rowWindowReady
            ? isDatabaseSnapshotFullyLoaded(dataSource)
            : null;
        }
        observeReadiness?.({
          hostPresent: Boolean(hostModel),
          rootPresent: Boolean(rootModel),
          rootReady: root?.ready === true,
          storageReady,
          rowWindowReady,
          snapshotFullyLoaded,
        });
        if (rowWindowReady && snapshotFullyLoaded) return true;
        await sleep(STANDARD_DATABASE_READY_POLL_MS);
      }
      return false;
    } finally {
      observedDataSource?.dispose();
    }
  }

  private async waitForDataSourcesReady(
    appBaseConfig: AppBaseConfig,
    sourceKeys: readonly string[],
    workspaceId: string,
    options: { retryPermissionLag?: boolean } = {}
  ): Promise<void> {
    const normalizedAppBaseConfig =
      normalizeAppBaseConfigDataSourceRefs(appBaseConfig);
    const dataSources = getAppBaseDataSources(normalizedAppBaseConfig);
    const reconciledDocIds = new Set<string>();
    const reconciledStorageDocIds = new Set<string>();
    for (const sourceKey of new Set(sourceKeys)) {
      const ref = dataSources[sourceKey];
      if (!ref) continue;
      const { doc, release } = this.docsService.open(ref.docId);
      try {
        // Server-owned provisioning persists the host Y.Doc before returning,
        // but a browser worker may still hold the pre-mutation snapshot. Pull
        // the authoritative remote delta through the existing reconciliation
        // contract before judging the referenced database block as missing.
        if (!reconciledDocIds.has(ref.docId)) {
          const pullDeadline = Date.now() + STANDARD_DATABASE_READY_TIMEOUT_MS;
          while (true) {
            try {
              await doc.workspace.engine.doc.pullRemote(ref.docId);
              break;
            } catch (error) {
              if (
                (!isCloudDocStorageConnectionNotReady(error) &&
                  (!options.retryPermissionLag ||
                    !isServerOwnedDataSourceAccessNotReady(error))) ||
                Date.now() >= pullDeadline
              ) {
                throw error;
              }
              // The server-owned mutation receipt is terminal, but an
              // already-connected sync worker can still finish its earlier
              // permission read before that commit becomes observable. Keep
              // the existing bounded readiness barrier on the same pull
              // contract; never surface this transient as an authoring error.
              await sleep(STANDARD_DATABASE_READY_POLL_MS);
            }
          }
          reconciledDocIds.add(ref.docId);
        }
        const storageDocId = createServerDatabaseRootDocId({
          workspaceId,
          databaseId: ref.databaseId,
        });
        if (!reconciledStorageDocIds.has(storageDocId)) {
          const pullDeadline = Date.now() + STANDARD_DATABASE_READY_TIMEOUT_MS;
          while (true) {
            try {
              await doc.workspace.engine.doc.pullRemote(storageDocId);
              break;
            } catch (error) {
              if (
                (!isCloudDocStorageConnectionNotReady(error) &&
                  (!options.retryPermissionLag ||
                    !isServerOwnedDataSourceAccessNotReady(error))) ||
                Date.now() >= pullDeadline
              ) {
                throw error;
              }
              await sleep(STANDARD_DATABASE_READY_POLL_MS);
            }
          }
          reconciledStorageDocIds.add(storageDocId);
        }
        const bsDoc = doc.blockSuiteDoc as Store | undefined;
        if (bsDoc) {
          bsDoc.load?.();
          await doc.waitForSyncReady();
        }
        if (!bsDoc?.root) {
          throw new Error(
            `ConfigProvisioner: doc ${ref.docId} is not ready (no root block)`
          );
        }
        let readiness = '';
        const ready = await this.waitForStandardDatabaseReady(
          bsDoc,
          {
            blockId: ref.blockId,
            databaseId: ref.databaseId,
            workspaceId,
          },
          state => {
            readiness = JSON.stringify(state);
          }
        );
        if (!ready) {
          throw new Error(
            `ConfigProvisioner: data source ${ref.key} storage is not ready` +
              (readiness ? ` (${readiness})` : '')
          );
        }
      } finally {
        release();
      }
    }
  }
}
